import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { MessageText } from "@/components/Chat/MessageStyles.ts"
import {
  RepositoryUrlMessageModel,
  type TextFieldModel
} from "@/components/Chat/NewProject/RepositoryUrlMessageModel.ts"
import { UserMessageAvatar } from "@/components/Chat/UserMessage.tsx"
import {
  StyledUserMessageBalloon,
  UserMessageContainer,
  UserMessageInner
} from "@/components/Chat/UserMessageStyles.ts"
import { UserMessageTopLine } from "@/components/Chat/UserMessageTopLine.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { AiChatMessageContent } from "@/lib/client/generated/TypeDefinitions.ts"
import type { ReactNode } from "react"

export const TextFieldMessageContainer = observer(({ message, text, children }: {
  message: ChatMessageModel
  text: string
  children: ReactNode
}) => {
  const { user } = message
  const isMyMessage = message.isMyMessage

  return (
    <UserMessageContainer isMyMessage={isMyMessage} isActive={message.active}>
      <UserMessageInner isEditing={message.editingModel !== null}>
        <UserMessageTopLine message={message} />

        <StyledUserMessageBalloon isMyMessage={isMyMessage}>
          <MessageText>
            <MarkdownWrapper text={text} />
          </MessageText>

          {children}
        </StyledUserMessageBalloon>
      </UserMessageInner>

      {!isMyMessage && <UserMessageAvatar user={user} />}
    </UserMessageContainer>
  )
})

export const TextFieldMessage = observer((props: {
  message: ChatMessageModel
  model: TextFieldModel
}) => {
  const model = props.model
  const value = model.value.get()
  const placeholder = ellipsize(model.placeholder)

  return (
    <TextFieldMessageContainer message={props.message} text={model.field}>
      <Vertical gap="16px">
        <InputContainer className={PromptInputStyles.Container}>
          <input
            value={value}
            onChange={model.handleChange}
            placeholder={placeholder}
            disabled={!model.editable.get()}
            readOnly={!model.editable.get()}
            className={cx(PromptInputStyles.Input, Input, Input_Positioned)}
          />
          <Span className={Input}>
            {value.length > placeholder.length ? value : placeholder}
          </Span>
        </InputContainer>

        {model.hasChanges.get() && (
          <Horizontal gap="8px" alignSelf="flex-end">
            <Button kind="alternative" onClick={model.cancel}>
              Cancel
            </Button>
            <Button kind="primary" disabled={!model.canSubmit.get()} onClick={model.save}>
              Update
            </Button>
          </Horizontal>

        )}
      </Vertical>
    </TextFieldMessageContainer>
  )
})

export const RepositoryUrlMessage = observer((props: {
  message: ChatMessageModel,
  content: AiChatMessageContent.InitProject.RepositoryUrl
}) => {
  const context = useOrgContext()

  const args = {
    context: context,
    value: props.content.url,
    taskId: props.message.taskId,
    editable: props.content.editable,
    placeholder: "Enter Git repository URL",
    field: "Repository URL:"
  }
  const model = useModel(() => new RepositoryUrlMessageModel(args), [args.context, args.taskId, args.value, args.editable])

  return (
    <TextFieldMessage model={model} message={props.message} />
  )
})

const InputContainer = styled.div`
  position: relative;
  height: 40px;
`

const Input = css`
  ${textStyles.smallFontSize};
  ${textStyles.breakWord};

  width: 100%;
  padding: 12px 16px;
  height: 40px;

  border: none;
`

const Span = styled.span`
  visibility: hidden;
`

const Input_Positioned = css`
  position: absolute;
  inset: 0;
`